import {ApiError, getCsrfToken, parseErrorBody} from "./genericApi.ts";
import { Konto } from "./kontoApi.ts";
import {useAppStore} from "../stores/appStore.ts";
import {cleanFilter} from "@/services/adminApi.ts";

export interface Actor {
    id: number,
    aard: string,
    ondernemingsnummer: string,
    naam: string,
    adres: string,
    telefoon: string,
    email: string
    }

export interface ActorInOrganisation extends Actor {
    relatie: string,
    verified: boolean
}

export interface ExpertCluster {
    id: number,
    naam: string,
    leden: ExpertSummary[]
}

export interface ExpertProfiel {
    id: number
    naam: string
    bedrijfsNaam: string
    ondernemingsNummer: string | null
    procesCertificaatNummer: string | null
    adres: { straatNummer: string, postcodeGemeente: string } | null
    telefoon: string | null
    email: string | null
}

export interface ActorInZaak {
    id: number,
    aard: string,
    ondernemingsnummer: string,
    naam: string,
    adres: string,
    telefoon: string,
    email: string,
    rollen: string[]
}

export interface ActorInZaakDTO {
    actorId: number,
    relatie: string,
    zaakId: number,
    taakId: number
}

export interface ActorenInZaak {
    byRole: {[key: string]: number},
    byId: {[key: number]: ActorInZaak}
}

export interface User {
    id: number,
    email: string,
    authService: string,
    persoon: number,
    idWithProvider: string
}

export interface Contact {
    id: any,
    voornaam: string,
    naam: string,
    adres: string,
    email: string,
    telefoon: string,
    btwnummer: string,
    rijksregisternr: string,
    voorOrganisatie: boolean,
    verbondenEntiteiten: VerbondenEntiteit[]
    }

export interface VerbondenEntiteit {
    organisatieId: number,
    naam: string,
    relatie: string
}

export interface PersoonRelatieOrganisatie {
    persoonId: number,
    relatie: string,
    organisatieId: number
}

export interface UserProfile {
    techUser: User,
    persoon: Actor,
    konto: Konto,
    oorsprong: string,
    gekozenUitvoerder: number | null,
    rol: string,
    nieuw: boolean,

    kontoActief: boolean,
    kontoGeldig: boolean,
    verbondenEntiteiten: VerbondenEntiteit[]
}

export interface ExpertSummary {
    id: number,
    naam: string,
    disciplines: string[],
    gemeente: string
}

export interface ExpertDetails {
    id: number,
    naam: string,
    ondernemingsnummer: string,
    telefoon: string,
    email: string,
    disciplines: string[],
    gemeente: string
}

export interface PersoonFilter {
    zoekterm?: string,
    rol?: string,
    door?: string
}

export interface OrganisatieFilter {
    aard: string,
    zoekterm?: string,
    ondernemingsNummer?: string
}

export interface PagedResult<T> {
    content: T[];
    total: number;
    page: number;
    size: number;
}

export async function zoekMensen(
    filter: PersoonFilter,
    page = 0,
    size = 20
): Promise<PagedResult<UserProfile>> {
    const params = new URLSearchParams({
        page: String(page),
        size: String(size),
        ...cleanFilter(filter)
    });
    const response = await fetch(`/api/admin/actoren?${params}`, {
        method: 'GET',
        credentials: 'include'
    });
    if (!response.ok) throw new Error(`zoekPersoon failed: ${response.status}`);
    return response.json();
}

export async function fetchUserProfile(id: number): Promise<UserProfile> {
    const response = await fetch(`/api/actoren/${id}`, {
        method: 'GET',
        credentials: 'include',
    });
    if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`)
    }
    const data = response.json()
    return data
}

export async function zoekOrganisaties(
    filter: OrganisatieFilter,
    page = 0,
    size = 20
): Promise<PagedResult<Actor>> {
    const params = new URLSearchParams({
        page: String(page),
        size: String(size),
        ...cleanFilter(filter)
    });
    const response = await fetch(`/api/admin/actoren/organisaties?${params}`, {
        method: 'GET',
        credentials: 'include'
    });
    if (!response.ok) throw new Error(`zoekOrganisatie failed: ${response.status}`);
    return response.json();
}

export async function lookupOrganisaties(q: string): Promise<Actor[]> {
    // same style as lookupOndernemingsnummer
    const response = await fetch(`/api/actoren/organisaties?q=${encodeURIComponent(q)}`, {
        method: 'GET',
        credentials: 'include'
    });
    if (!response.ok) throw new Error(`zoek Organisatie failed: ${response.status}`);
    return response.json();
}


export async function getPersonalia(id: any): Promise<Contact> {

    if (id == null) return null

    const response = await fetch('/api/actoren/personalia/' + id, {
        method: 'GET',
        credentials: 'include',
    })
    if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`)
    }
    const data = response.json()
    return data

}

export async function getActor(id: any): Promise<Actor> {
    if (id == null) return null
    const response = await fetch('/api/actoren/actor/' + id, {
        method: 'GET',
        credentials: 'include',
    })
    if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`)
    }
    const data = response.json()
    return data;
}


export async function saveActor(actor: Actor): Promise<number> {
    const token = getCsrfToken();

    const response = await fetch('/api/actoren/actor', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-XSRF-TOKEN': token || '',
        },
        body: JSON.stringify(actor),
        credentials: 'include',
    })

    if (!response.ok) {
        const { message, details } = await parseErrorBody(response)
        console.error('❌ Error saving actor:', message, details)
        throw new ApiError(message, "" + response.status)
    }
    return response.json()
}

export async function changeContactPersoon(zaakId: number, actorId: number) {
    const token = getCsrfToken();

    const response = await fetch('/api/zaken/' + zaakId + '/switchcontact/' + actorId, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            'X-XSRF-TOKEN': token || '',
        },
        credentials: 'include'
    })

    if (!response.ok) {
        const { message, details } = await parseErrorBody(response)
        console.error('❌ Error saving actor:', message, details)
        throw new ApiError(message, "" + response.status)
    }
    return true;
}


export async function getMe(): Promise<UserProfile> {
    console.log('>>>>>GETTING ME')
    const response = await fetch('/api/actoren/me', {
        method: 'GET',
        credentials: 'include',
    })
    if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`)
    }
    const identiteit: UserProfile = await response.json()
    return identiteit;
}

export async function getFreshMe(): Promise<UserProfile> {
    console.log('>>>>>GETTING ME')
    const response = await fetch('/api/actoren/freshme', {
        method: 'GET',
        credentials: 'include',
    })
    if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`)
    }
    const identiteit: UserProfile = await response.json()
    return identiteit;
}

export async function saveProfile(userProfile : UserProfile ) : Promise<UserProfile> {
    const token = getCsrfToken();
    const response = await fetch('/api/actoren/profile', {

        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-XSRF-TOKEN': token || '',
        },
        body: JSON.stringify(userProfile),
        credentials: 'include', // if you need cookies/session
    })

    if (!response.ok) {
        const text = await response.text()
        console.error('❌ Error:', text)
        throw new Error(`Failed to save draft: ${response.statusText}`)
    }
    return response.json()
}


export async function requestUserOrganisationLink(p : number, r : string, o : number) {

    let record = {persoonId: p, relatie: r, organisatieId: o} as PersoonRelatieOrganisatie;

    const token = getCsrfToken();
    const response = await fetch('/api/actoren/requestlink', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-XSRF-TOKEN': token || '',
        },
        body: JSON.stringify(record),
        credentials: 'include', // if you need cookies/session
    })
    if (!response.ok) {
        const text = await response.text()
        console.error('❌ Error:', text)
        throw new Error(`Failed to save draft: ${response.statusText}`)
    }
    return;
}

export async function confirmUserOrganisationLink(p : number, r : string, o : number) {
    let record = {persoonId: p, relatie: r, organisatieId: o} as PersoonRelatieOrganisatie;
    const token = getCsrfToken();
    const response = await fetch('/api/actoren/collega/confirm', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-XSRF-TOKEN': token || '',
        },
        body: JSON.stringify(record),
        credentials: 'include', // if you need cookies/session
    })
    if (!response.ok) {
        const text = await response.text()
        console.error('❌ Error:', text)
        throw new Error(`Failed to save draft: ${response.statusText}`)
    }
    return;
}


export async function linkUserAndOrganisation(p : number, r : string, o : number) {

    let record = {persoonId: p, relatie: r, organisatieId: o} as PersoonRelatieOrganisatie;

    const token = getCsrfToken();
    const response = await fetch('/api/actoren/link', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-XSRF-TOKEN': token || '',
        },
        body: JSON.stringify(record),
        credentials: 'include', // if you need cookies/session
    })
    if (!response.ok) {
        const text = await response.text()
        console.error('❌ Error:', text)
        throw new Error(`Failed to save draft: ${response.statusText}`)
    }
    return;
    }

export async function unlinkUserFromOrganisation(p : number, r : string, o : number) {

    let record = {persoonId: p, relatie: r, organisatieId: o} as PersoonRelatieOrganisatie;

    const token = getCsrfToken();
    const response = await fetch('/api/actoren/unlink', {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
            'X-XSRF-TOKEN': token || '',
        },
        body: JSON.stringify(record),
        credentials: 'include', // if you need cookies/session
    })
    if (!response.ok) {
        const text = await response.text()
        console.error('❌ Error:', text)
        throw new Error(`Failed to save draft: ${response.statusText}`)
    }
    return;
}

export async function unlinkExpertFromOrganisation(p: number, o : number) {
    unlinkUserFromOrganisation(p, 'IS_FAVORIETE_EXPERT', o);
}

export async function isMember(id: number, clusterId: number) {

}

export async function listMyClusters(expertId: number) {
    const response = await fetch('/api/actoren/' + expertId + '/clusters', {
        method: 'GET',
        credentials: 'include',
    })
    if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`)
    }
    const clusters: ExpertCluster[] = await response.json()
    return clusters;
}

function filterNummer(nummer: string) {
    let fnummer = nummer.startsWith('BE0') ? nummer.substring(3) : nummer;
    return fnummer.replace(/[^0-9]/g, '');
}

export async function lookupOndernemingsnummer(nummer: string) {
    // neemt enkel ouderwets BTW nummer ...
    let btw = filterNummer(nummer);
    const response = await fetch('/api/externzoeken/onderneming/' + btw, {
        method: 'GET',
        credentials: 'include',
    })
    if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`)
    }
    const data = response.json()
    return data
}

export async function getExpertProfiel(id:number) : Promise<ExpertProfiel> {
    const response = await fetch('/api/zaken/' + id + '/deskundige', {
        method: 'GET',
        credentials: 'include',
    })
    if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`)
    }
    const data = response.json()
    return data
}

export async function listExperts(q: string) : Promise<ExpertSummary[]> {
    const response = await fetch('/api/actoren/expert/zoeken?q=' + q, {
        method: 'GET',
        credentials: 'include',
    })
    if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`)
    }
    const data = response.json()
    return data
}

export async function getMijnExperts(opdrachtgeverId: number) : Promise<ExpertSummary[]> {

    const response = await fetch('/api/actoren/expert/favouritesof/' + opdrachtgeverId, {
        method: 'GET',
        credentials: 'include',
    })
    if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`)
    }
    const data = response.json()
    return data
}


export async function listExpertsByNisAndCompetences(nis: string, comp : string[]) : Promise<ExpertSummary[]> {

    const response = await fetch('/api/actoren/expertsbij?nis=' + nis
        + '&disc=' + comp.join('&disc='), {
        method: 'GET',
        credentials: 'include',
    })
    if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`)
    }
    const data = response.json()
    return data
}

export async function listExpertsByNis(nis: string) : Promise<ExpertSummary[]> {

    const response = await fetch('/api/actoren/expertsbij?nis=' + nis, {
        method: 'GET',
        credentials: 'include',
    })
    if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`)
    }
    const data = response.json()
    return data
}


export async function getExpertDetails(id: number) : Promise<ExpertDetails> {
    const response = await fetch('/api/actoren/expert/' + id, {
        method: 'GET',
        credentials: 'include',
    })
    if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`)
    }
    const data = response.json()
    return data
}

export async function listCollegae(orgId: number) : Promise<ActorInOrganisation[]> {
    const response = await fetch('/api/actoren/collegae/' + orgId, {
        method: 'GET',
        credentials: 'include',
    })
    if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`)
    }
    const data = response.json()
    return data
}

export async function eilandLogin(email: string, authProvider: string) {

    let record = { email: email, authProvider: authProvider }
    const token = getCsrfToken();

    const response = await fetch('/api/public/eiland/login', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-XSRF-TOKEN': token || '',
        },
        body: JSON.stringify(record),
        credentials: 'include', // if you need cookies/session
    })
    if (!response.ok) {
        return false;
    }
    return true;
}
